import { useState, useEffect } from 'react';

/**
 * Custom hook to detect device type and screen size
 * Returns mobile, tablet, desktop status and screen dimensions
 */
export function useDeviceDetect() {
  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isTouchDevice: false,
    screenWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    screenHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Check if touch device
      const isTouchDevice = 'ontouchstart' in window || 
                           navigator.maxTouchPoints > 0 || 
                           navigator.msMaxTouchPoints > 0;
      
      // Mobile: <= 768px
      // Tablet: 769px - 1024px
      // Desktop: > 1024px
      const isMobile = width <= 768;
      const isTablet = width > 768 && width <= 1024;
      const isDesktop = width > 1024;

      setDeviceInfo({
        isMobile,
        isTablet,
        isDesktop,
        isTouchDevice,
        screenWidth: width,
        screenHeight: height,
      });
    };

    // Check on mount
    checkDevice();

    // Add resize listener with debounce
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkDevice, 150);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', checkDevice);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', checkDevice);
      clearTimeout(timeoutId);
    };
  }, []);

  return deviceInfo;
}

/**
 * Detect if user is on iOS device
 */
export function isIOS() {
  if (typeof window === 'undefined') return false;
  
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Detect if user is on Android device
 */
export function isAndroid() {
  if (typeof window === 'undefined') return false;
  
  return /Android/.test(navigator.userAgent);
}

/**
 * Get device OS type
 */
export function getDeviceOS() {
  if (isIOS()) return 'iOS';
  if (isAndroid()) return 'Android';
  if (typeof window !== 'undefined' && /Windows/.test(navigator.userAgent)) return 'Windows';
  if (typeof window !== 'undefined' && /Mac/.test(navigator.userAgent)) return 'MacOS';
  return 'Unknown';
}
